/* eslint-disable object-curly-newline */
/* eslint-disable object-property-newline */
import axios from 'axios'
import { normalize } from 'normalizr'
import auth from './auth'
import { teamsSchema, teamSchema, kudosSchema } from '@/helpers/schemas'

const API_URL = process.env.VUE_APP_API_URL

const noAuth = {
  headers: {
    Authorization: ''
  }
}

const setAuthHeaders = (token) => {
  const HEADERS = {
    'X-Requested-With': 'XMLHttpRequest'
  }
  if (token) {
    HEADERS.Authorization = `Token ${token}`
  }
  axios.defaults.headers.common = HEADERS
}

const setToken = (token) => {
  auth.setToken(token)
  setAuthHeaders(token)
}

export default () => {
  return {
    restoreHeaders() {
      setAuthHeaders(auth.getToken())
    },

    getToken() {
      return auth.getToken()
    },

    setToken(token) {
      setToken(token)
    },

    getWorkspaceSlug() {
      return auth.getWorkspaceSlug()
    },

    setWorkspaceSlug(slug) {
      auth.setWorkspaceSlug(slug)
    },

    clearWorkspaceSlug() {
      auth.clearWorkspaceSlug()
    },

    fetchFeeds() {
      return new Promise((resolve, reject) => {
        axios
          .get(`${API_URL}/feed`)
          .then(response => resolve(response.data))
          .catch(response => reject(response.data))
      })
    },

    fetchFeedbacks(companySlug, userId) {
      return new Promise((resolve, reject) => {
        axios
          .get(`${API_URL}/companies/${companySlug}/users/${userId}/feedback`)
          .then(response => resolve(response.data))
          .catch(response => reject(response.data))
      })
    },

    fetchFeedbacksMe(companySlug) {
      return new Promise((resolve, reject) => {
        axios
          .get(`${API_URL}/companies/${companySlug}/users/me/feedback`)
          .then(response => resolve(response.data))
          .catch(response => reject(response.data))
      })
    },

    fetchFeedbacksTeam(companySlug, teamId) {
      return new Promise((resolve, reject) => {
        axios
          .get(`${API_URL}/companies/${companySlug}/team/${teamId}/feedback`)
          .then(response => resolve(response.data))
          .catch(response => reject(response.data))
      })
    },

    fetchCompanyAdmins(companySlug) {
      return new Promise((resolve, reject) => {
        axios
          .get(`${API_URL}/companies/${companySlug}/admins`)
          .then(response => {
            auth.setWorkspaceSlug(companySlug)
            resolve(response.data)
          })
          .catch(error => reject(error.response.data))
      })
    },

    addCompanyAdmin(companySlug, memberId) {
      return new Promise((resolve, reject) => {
        axios
          .post(`${API_URL}/companies/${companySlug}/admins`, { user: memberId })
          .then(response => resolve(response.data))
          .catch(error => reject(error.response.data))
      })
    },

    deleteCompanyAdmin(companySlug, memberId) {
      return new Promise((resolve, reject) => {
        axios
          .delete(`${API_URL}/companies/${companySlug}/admins`, { data: { user: memberId } })
          .then(response => resolve(response.data))
          .catch(error => reject(error.response.data))
      })
    },

    fetchTeams(companySlug) {
      return new Promise((resolve, reject) => {
        axios
          .get(`${API_URL}/companies/${companySlug}/team`)
          .then(response => resolve(normalize(response.data, teamsSchema)))
          .catch(response => reject(response.data))
      })
    },

    fetchTeam(companySlug, teamId) {
      return new Promise((resolve, reject) => {
        axios
          .get(`${API_URL}/companies/${companySlug}/team/${teamId}`)
          .then(response => resolve(normalize(response.data, teamSchema)))
          .catch(response => reject(response.data))
      })
    },

    createTeam(companySlug, formData) {
      return new Promise((resolve, reject) => {
        axios
          .post(`${API_URL}/companies/${companySlug}/team/`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          })
          .then(response => resolve(normalize(response.data, teamSchema)))
          .catch(error => reject(error.response.data))
      })
    },

    updateTeam(companySlug, teamId, formData) {
      return new Promise((resolve, reject) => {
        axios
          .patch(`${API_URL}/companies/${companySlug}/team/${teamId}`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          })
          .then(response => resolve(normalize(response.data, teamSchema)))
          .catch(error => reject(error.response.data))
      })
    },

    addTeamMember(companySlug, teamId, memberId) {
      return new Promise((resolve, reject) => {
        axios
          .post(`${API_URL}/companies/${companySlug}/team/${teamId}/members`, { user: memberId })
          .then(response => resolve(response.data))
          .catch(error => reject(error.response.data))
      })
    },

    deleteTeamMember(companySlug, teamId, memberId) {
      return new Promise((resolve, reject) => {
        axios
          .delete(`${API_URL}/companies/${companySlug}/team/${teamId}/members`, { data: { user: memberId } })
          .then(response => resolve(response.data))
          .catch(error => reject(error.response.data))
      })
    },

    fetchKudos() {
      return new Promise((resolve, reject) => {
        axios
          .get(`${API_URL}/kudos/library`, noAuth)
          .then(response => resolve(normalize(response.data, kudosSchema)))
          .catch(response => reject(response.data))
      })
    },

    fetchUserKudos(profileId) {
      return new Promise((resolve, reject) => {
        axios
          .get(`${API_URL}/users/profile/${profileId}/kudos`)
          .then(response => resolve(response.data))
          .catch(response => reject(response.data))
      })
    },

    fetchUserMeKudos() {
      return new Promise((resolve, reject) => {
        axios
          .get(`${API_URL}/users/profile/me/kudos`)
          .then(response => resolve(response.data))
          .catch(response => reject(response.data))
      })
    },

    fetchTeamKudos(companySlug, teamId) {
      return new Promise((resolve, reject) => {
        axios
          .get(`${API_URL}/companies/${companySlug}/team/${teamId}/kudos`)
          .then(response => resolve(response.data))
          .catch(response => reject(response.data))
      })
    },

    sendKudo(companySlug, data) {
      return new Promise((resolve, reject) => {
        axios
          .post(`${API_URL}/companies/${companySlug}/kudos/send`, data)
          .then(response => resolve(response.data))
          .catch(error => reject(error.response.data))
      })
    },

    fetchKudoProficient(companySlug, kudoId) {
      return new Promise((resolve, reject) => {
        axios
          .get(`${API_URL}/companies/${companySlug}/kudos/${kudoId}/proficient-people`)
          .then(response => resolve(response.data))
          .catch(response => reject(response.data))
      })
    },

    fetchUserThanks(companySlug, userId) {
      return new Promise((resolve, reject) => {
        axios
          .get(`${API_URL}/companies/${companySlug}/users/${userId}/thanks`)
          .then(response => resolve(response.data))
          .catch(response => reject(response.data))
      })
    },

    fetchCompany(companySlug) {
      return new Promise((resolve, reject) => {
        axios
          .get(`${API_URL}/companies/${companySlug}`, noAuth)
          .then(response => {
            auth.setWorkspaceSlug(companySlug)
            resolve(response.data)
          })
          .catch(error => reject(error.response.data))
      })
    },

    signIn(data) {
      return new Promise((resolve, reject) => {
        axios
          .post(`${API_URL}/login/`, data, noAuth)
          .then(response => {
            const { token } = response.data
            setToken(token)
            resolve(data)
          })
          .catch(error => reject(error.response.data))
      })
    },

    signOut() {
      auth.clearToken()
    },

    changePassword(oldPassword, newPassword1, newPassword2) {
      return new Promise((resolve, reject) => {
        axios
          .post(`${API_URL}/users/profile/me/password/change`, { oldPassword, newPassword1, newPassword2 })
          .then(response => {
            const data = response.data
            resolve(data)
          })
          .catch(error => reject(error.response.data))
      })
    },

    forgotWorkspace(email) {
      return new Promise((resolve, reject) => {
        axios
          .post(`${API_URL}/company-url/forgot`, { email })
          .then(response => {
            const data = response.data
            resolve(data)
          })
          .catch(error => reject(error.response.data))
      })
    },

    companySignUp(data) {
      return new Promise((resolve, reject) => {
        axios
          .post(`${API_URL}/company-signup/`, data, noAuth)
          .then(response => {
            const data = response.data
            resolve(data)
          })
          .catch(error => reject(error.response.data))
      })
    },

    companySignUpVerify(data) {
      return new Promise((resolve, reject) => {
        axios
          .post(`${API_URL}/company-signup/verify`, data, noAuth)
          .then(response => {
            const data = response.data
            auth.setWorkspaceSlug(data.details.slug)
            resolve(data)
          })
          .catch(error => reject(error.response.data))
      })
    },

    companySignUpCheckName(data) {
      return new Promise((resolve, reject) => {
        axios
          .post(`${API_URL}/company-signup/check-name`, data, noAuth)
          .then(() => resolve())
          .catch(error => reject(error.response.data))
      })
    },

    managerSignUp(data) {
      return new Promise((resolve, reject) => {
        axios
          .post(`${API_URL}/manager-signup`, data, noAuth)
          .then(response => {
            const { token, profile } = response.data
            setToken(token)
            resolve(profile)
          })
          .catch(error => reject(error.response.data))
      })
    },

    teamMemberSignUp(data) {
      return new Promise((resolve, reject) => {
        axios
          .post(`${API_URL}/team-member-signup`, data, noAuth)
          .then(response => {
            const { token, profile } = response.data
            setToken(token)
            resolve(profile)
          })
          .catch(error => reject(error.response.data))
      })
    },

    teamInvitationVerify(data) {
      return new Promise((resolve, reject) => {
        axios
          .post(`${API_URL}/team-invitation/verify`, data, noAuth)
          .then(response => {
            const data = response.data
            auth.setWorkspaceSlug(data.details.team.company.slug)
            resolve(data)
          })
          .catch(error => reject(error.response.data))
      })
    },

    fetchProfile(profileId) {
      return new Promise((resolve, reject) => {
        axios
          .get(`${API_URL}/users/profile/${profileId}`)
          .then(response => resolve(response.data))
          .catch(response => reject(response.data))
      })
    },

    fetchProfileMe() {
      return new Promise((resolve, reject) => {
        axios
          .get(`${API_URL}/users/profile/me`)
          .then(response => resolve(response.data))
          .catch(response => reject(response.data))
      })
    },

    // updateProfile(profileId, formData) {
    //   return new Promise((resolve, reject) => {
    //     axios
    //       .patch(`${API_URL}/users/profile/${profileId}`, formData, {
    //         headers: {
    //           'Content-Type': 'multipart/form-data'
    //         }
    //       })
    //       .then(response => resolve(response.data))
    //       .catch(error => reject(error.response.data))
    //   })
    // },

    updateProfileMe(formData) {
      return new Promise((resolve, reject) => {
        axios
          .patch(`${API_URL}/users/profile/me`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          })
          .then(response => resolve(response.data))
          .catch(error => reject(error.response.data))
      })
    },

    recognitionInvite(companySlug, data) {
      return new Promise((resolve, reject) => {
        axios
          .post(`${API_URL}/companies/${companySlug}/recognition/invite`, data)
          .then(response => resolve(response.data))
          .catch(error => reject(error.response.data))
      })
    },

    kudoRecognition(key) {
      return new Promise((resolve, reject) => {
        axios
          .get(`${API_URL}/kudos/recognition/${key}/give`)
          .then(response => resolve(response.data))
          .catch(response => reject(response.data))
      })
    },

    sendKudoRecognition(key, data) {
      return new Promise((resolve, reject) => {
        axios
          .post(`${API_URL}/kudos/recognition/${key}/give`, data, noAuth)
          .then(response => resolve(response.data))
          .catch(error => reject(error.response.data))
      })
    },

    previewUserInvitation(companySlug, teamName) {
      return new Promise((resolve, reject) => {
        axios
          .get(`${API_URL}/companies/${companySlug}/preview-invitation?name=${encodeURI(teamName)}`)
          .then(response => resolve(response.data))
          .catch(response => reject(response.data))
      })
    },

    inviteUsers(companySlug, data) {
      return new Promise((resolve, reject) => {
        axios
          .post(`${API_URL}/companies/${companySlug}/invite-users/`, data)
          .then(response => resolve(response.data))
          .catch(error => reject(error.response.data))
      })
    },

    getInvitationLink(companySlug, teamId) {
      return new Promise((resolve, reject) => {
        axios
          .post(`${API_URL}/companies/${companySlug}/team/${teamId}/invitation-link`)
          .then(response => resolve(response.data))
          .catch(error => reject(error.response.data))
      })
    },

    validateInvitationLink(companySlug, key) {
      return new Promise((resolve, reject) => {
        axios
          .get(`${API_URL}/companies/${companySlug}/invitation-link/${key}`, noAuth)
          .then(response => resolve(response.data))
          .catch(response => reject(response.data))
      })
    },

    inviteUserByInvitationLink(companySlug, key, email) {
      return new Promise((resolve, reject) => {
        axios
          .post(`${API_URL}/companies/${companySlug}/invitation-link-signup`, { key, email }, noAuth)
          .then(response => resolve(response.data))
          .catch(error => reject(error.response.data))
      })
    },

    fetchReceivedMessages(companySlug) {
      return new Promise((resolve, reject) => {
        axios
          .get(`${API_URL}/companies/${companySlug}/messages/received`)
          .then(response => resolve(response.data))
          .catch(response => reject(response.data))
      })
    },

    fetchSentMessages(companySlug) {
      return new Promise((resolve, reject) => {
        axios
          .get(`${API_URL}/companies/${companySlug}/messages/sent`)
          .then(response => resolve(response.data))
          .catch(response => reject(response.data))
      })
    },

    fetchCountNewMessages(companySlug) {
      return new Promise((resolve, reject) => {
        axios
          .get(`${API_URL}/companies/${companySlug}/messages/new/count`)
          .then(response => resolve(response.data))
          .catch(response => reject(response.data))
      })
    },

    seenMessage(companySlug, messageId) {
      return new Promise((resolve, reject) => {
        axios
          .put(`${API_URL}/companies/${companySlug}/messages/${messageId}/seen`)
          .then(response => resolve(response.data))
          .catch(error => reject(error.response.data))
      })
    },

    sendMessage(companySlug, data) {
      return new Promise((resolve, reject) => {
        axios
          .post(`${API_URL}/companies/${companySlug}/messages`, data)
          .then(response => resolve(response.data))
          .catch(error => reject(error.response.data))
      })
    },

    fetchTeamMetrics(companySlug, teamId, timespan) {
      return new Promise((resolve, reject) => {
        axios
          .get(`${API_URL}/companies/${companySlug}/team/${teamId}/metrics`)
          .then(response => resolve(response.data))
          .catch(response => reject(response.data))
      })
    },

    fetchTeamExpertise(companySlug, teamId, timespan) {
      return new Promise((resolve, reject) => {
        axios
          .get(`${API_URL}/companies/${companySlug}/team/${teamId}/expertise`)
          .then(response => resolve(response.data))
          .catch(response => reject(response.data))
      })
    },

    fetchTeamWorkStandards(companySlug, teamId, timespan) {
      return new Promise((resolve, reject) => {
        axios
          .get(`${API_URL}/companies/${companySlug}/team/${teamId}/work-standards`)
          .then(response => resolve(response.data))
          .catch(response => reject(response.data))
      })
    },

    fetchTeamMostEncouraging(companySlug, teamId, timespan) {
      return new Promise((resolve, reject) => {
        axios
          .get(`${API_URL}/companies/${companySlug}/team/${teamId}/most-encouraging-other`)
          .then(response => resolve(response.data))
          .catch(response => reject(response.data))
      })
    },

    fetchPrioritiesMe() {
      return new Promise((resolve, reject) => {
        axios
          .get(`${API_URL}/priorities`)
          .then(response => resolve(response.data))
          .catch(response => reject(response.data))
      })
    },

    fetchPriorities(userId) {
      return new Promise((resolve, reject) => {
        axios
          .get(`${API_URL}/priorities/users/${userId}`)
          .then(response => resolve(response.data))
          .catch(response => reject(response.data))
      })
    },

    addPriority(data) {
      return new Promise((resolve, reject) => {
        axios
          .post(`${API_URL}/priorities`, data)
          .then(response => resolve(response.data))
          .catch(error => reject(error.response.data))
      })
    },

    updatePriority(priorityId, data) {
      return new Promise((resolve, reject) => {
        axios
          .put(`${API_URL}/priorities/${priorityId}`, data)
          .then(response => resolve(response.data))
          .catch(error => reject(error.response.data))
      })
    },

    toggleDonePriority(priorityId) {
      return new Promise((resolve, reject) => {
        axios
          .put(`${API_URL}/priorities/${priorityId}/toggle`)
          .then(response => resolve(response.data))
          .catch(error => reject(error.response.data))
      })
    },

    deletePriority(priorityId) {
      return new Promise((resolve, reject) => {
        axios
          .delete(`${API_URL}/priorities/${priorityId}`)
          .then(response => resolve(response.data))
          .catch(error => reject(error.response.data))
      })
    },

    slackState(companySlug) {
      return new Promise((resolve, reject) => {
        axios
          .get(`${API_URL}/companies/${companySlug}/slack`)
          .then(response => resolve(response.data))
          .catch(response => reject(response.data))
      })
    },

    msTeamsState(companySlug) {
      return new Promise((resolve, reject) => {
        axios
          .get(`${API_URL}/companies/${companySlug}/ms_teams`)
          .then(response => resolve(response.data))
          .catch(response => reject(response.data))
      })
    }
  }
}
